import { toothOrderRows, permanent28, multiToothCodes, codeByKey, resolveColor, toothColors } from './odontogramConfig';

const cpodCCodes = new Set([
  codeByKey.caries,
  codeByKey.temporaryRestoration,
  codeByKey.temporaryCrown,
  codeByKey.radicalRemnant,
]);
const cpodPCodes = new Set([codeByKey.missingTooth, multiToothCodes.totalEdentulous]);
const cpodOCodes = new Set([
  codeByKey.restoration,
  codeByKey.definiteCrown,
  codeByKey.pulpTreatmentCompleted,
]);

const trackedConditions = {
  caries: new Set([codeByKey.caries]),
  missing: new Set([codeByKey.missingTooth]),
  remanentRoot: new Set([codeByKey.radicalRemnant]),
  necrosis: new Set([codeByKey.pulpTreatmentPending]),
  badState: new Set(),
};

const matchesAny = (types, codeSet) => {
  for (const code of codeSet) { if (types.has(code)) return true; }
  return false;
};

export const expandMultiToothRange = (fromTooth, toTooth) => {
  const from = parseInt(fromTooth, 10);
  const to = parseInt(toTooth, 10);
  if (isNaN(from) || isNaN(to)) return [];

  const fromRowIndex = toothOrderRows.findIndex(row => row.includes(from));
  const toRowIndex = toothOrderRows.findIndex(row => row.includes(to));
  if (fromRowIndex === -1 || toRowIndex === -1) return [];

  if (fromRowIndex === toRowIndex) {
    const row = toothOrderRows[fromRowIndex];
    const fromPos = row.indexOf(from);
    const toPos = row.indexOf(to);
    return row.slice(Math.min(fromPos, toPos), Math.max(fromPos, toPos) + 1);
  }

  const firstRowIndex = Math.min(fromRowIndex, toRowIndex);
  const lastRowIndex = Math.max(fromRowIndex, toRowIndex);
  const firstTooth = fromRowIndex <= toRowIndex ? from : to;
  const lastTooth = fromRowIndex <= toRowIndex ? to : from;

  const result = [];
  const firstRow = toothOrderRows[firstRowIndex];
  result.push(...firstRow.slice(firstRow.indexOf(firstTooth)));

  for (let rowIndex = firstRowIndex + 1; rowIndex < lastRowIndex; rowIndex++) {
    result.push(...toothOrderRows[rowIndex]);
  }

  const lastRow = toothOrderRows[lastRowIndex];
  result.push(...lastRow.slice(0, lastRow.indexOf(lastTooth) + 1));

  return result;
};

export const groupTeethByRow = (teeth) => {
  const segments = [];
  toothOrderRows.forEach(row => {
    const teethInRow = teeth.filter(tooth => row.includes(tooth));
    if (teethInRow.length >= 1) {
      const indices = teethInRow.map(tooth => row.indexOf(tooth));
      segments.push({
        from: row[Math.min(...indices)],
        to: row[Math.max(...indices)],
      });
    }
  });
  return segments;
};

export const isToothInBadState = (toothNum, toothFindings = {}, multiToothFindingsForTooth = {}) => {
  const findings = toothFindings[toothNum] || [];
  const multiFindings = multiToothFindingsForTooth[toothNum] || [];
  const allFindings = [...findings, ...multiFindings];
  return allFindings.some(finding => {
    const color = resolveColor(finding);
    return color?.toLowerCase() === toothColors.pathological.toLowerCase();
  });
};

export const computeOdontogramCounts = (toothFindings, multiToothFindings = [], cpodExcludedTeeth = []) => {
  const trackedTeeth = {};
  Object.keys(trackedConditions).forEach(key => { trackedTeeth[key] = []; });

  let cpodC = 0, cpodP = 0, cpodO = 0;
  const excludedSet = new Set(cpodExcludedTeeth.map(Number));

  const multiToothTypes = {};
  const multiToothFindingsForTooth = {};
  multiToothFindings.forEach((finding) => {
    const teeth = finding.teeth || expandMultiToothRange(finding.fromTooth, finding.toTooth);
    teeth.forEach((toothNum) => {
      if (!multiToothTypes[toothNum]) multiToothTypes[toothNum] = new Set();
      multiToothTypes[toothNum].add(finding.code || finding.type);

      if (!multiToothFindingsForTooth[toothNum]) multiToothFindingsForTooth[toothNum] = [];
      multiToothFindingsForTooth[toothNum].push(finding);
    });
  });

  const allToothNums = new Set([
    ...Object.keys(toothFindings).map(Number),
    ...Object.keys(multiToothTypes).map(Number),
  ]);
  const getTypesForTooth = (toothNum) => {
    const findings = toothFindings[toothNum] || [];
    const types = new Set(findings.map((finding) => finding.type));
    const multiTypes = multiToothTypes[toothNum];
    if (multiTypes) multiTypes.forEach((type) => types.add(type));
    return types;
  };

  allToothNums.forEach((toothNum) => {
    const types = getTypesForTooth(toothNum);
    Object.entries(trackedConditions).forEach(([key, codeSet]) => {
      if (key === 'badState') {
        if (isToothInBadState(toothNum, toothFindings, multiToothFindingsForTooth)) {
          trackedTeeth.badState.push(toothNum);
        }
      } else {
        if (matchesAny(types, codeSet)) trackedTeeth[key].push(toothNum);
      }
    });
  });

  permanent28.forEach((toothNum) => {
    if (excludedSet.has(toothNum)) return;
    const types = getTypesForTooth(toothNum);
    if (matchesAny(types, cpodCCodes)) cpodC++;
    else if (matchesAny(types, cpodPCodes)) cpodP++;
    else if (matchesAny(types, cpodOCodes)) cpodO++;
  });

  const result = { cpodC, cpodP, cpodO, cpodTotal: cpodC + cpodP + cpodO };
  Object.entries(trackedTeeth).forEach(([key, teeth]) => {
    result[key] = { count: teeth.length, teeth: teeth.sort((a, b) => a - b) };
  });

  return result;
};
